import { AppAction } from "../../../root/AppAction";
import { GetClipTimesFromCliplist, GetTotalTimeFromClipList } from "../../../utils/TimelineUtils";
import {
    Folder,
    Footage,
    Timeline,
    TimelineInfo,
    TimelinePlayInfo,
    UploadTimelineInfo,
} from "../../models/Timeline";
import DirectoryEvents from "../events/DirectoryEvents";
import TimelineEvents from "../events/TimelineEvents";
import VideoInfoEvents from "../events/VideoInfoEvents";

export interface TimelineStore {
    uneditedFootages: Footage[];
    timelineInfoList: TimelineInfo[];
    isSuccessUploadCancel: boolean;
    isSuccessGetTimelines: boolean;
    recentFootages: Footage[];
    isTimelineCreateStatus: boolean;
    timeline: Timeline | null;
    timelinePlayInfo: TimelinePlayInfo;
    uploadTimeline: UploadTimelineInfo | null;
    footageList: Footage[];
    footageUrl: string; //フッテージの開始・終了時間設定用（タイムラインにクリップ追加の為）
    directoryList: Folder[];
    timelineError: string | null;
    isClipMove: boolean;
    isLoading: boolean;
    isChangeClipListHistory: boolean;
    isLoadingUploadTimeline: boolean;
    isSuccessUploadTimeline: boolean;
    isLoadingUploadFootage: boolean;
    isFailedUploadTimeline: boolean;
    isAddClipToTimelineComplete: boolean;
    isSuccessDeleteClip: boolean;
    isFailedDeleteClip: boolean;
    isSuccessGetTimeline: boolean;
    isFailedGetTimeline: boolean;
    duplicateNameError: string;
}

const initialTimelineStore: TimelineStore = {
    uneditedFootages: [],
    timelineInfoList: [],
    isSuccessUploadCancel: false,
    isSuccessGetTimelines: false,
    recentFootages: [],
    isTimelineCreateStatus: false,
    timeline: null,
    timelinePlayInfo: {
        sourceIndex: 0,
        sourceLength: 0,
        clipTimes: [],
        currentTime: 0,
        totalTime: 0,
        isFinished: false,
        isPlay: false,
        isHandleKeyboard: true,
        shootCutKeyId: -1
    },
    uploadTimeline: null,
    footageList: [],
    footageUrl: "",
    directoryList: [],
    timelineError: null,
    isClipMove: false,
    isLoading: false,
    isChangeClipListHistory: false,
    isLoadingUploadTimeline: false,
    isSuccessUploadTimeline: false,
    isLoadingUploadFootage: false,
    isFailedUploadTimeline: false,
    isAddClipToTimelineComplete: false,
    isSuccessDeleteClip: false,
    isFailedDeleteClip: false,
    isSuccessGetTimeline: false,
    isFailedGetTimeline: false,
    duplicateNameError: "",
};

export function timeline(state: TimelineStore = initialTimelineStore, action: AppAction) {
    switch (action.type) {
        // 未編集のフッテージ一覧取得
        case TimelineEvents.GetUneditedFootagesComplete:
            return {
                ...state,
                uneditedFootages: action.footage,
            };

        // タイムライン取得
        case TimelineEvents.GetTimelinesStarted:
            return {
                ...state,
                isSuccessGetTimelines: false,
                isSuccessGetTimeline: false,
                isFailedGetTimeline: false,
            };
        case TimelineEvents.GetTimelinesComplete:
            return {
                ...state,
                timelineInfoList: action.timeline,
                isSuccessGetTimelines: true,
                timeline: {
                    ...state.timeline,
                    clipList: [],
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    sourceLength: 0,
                    clipTimes: [],
                    currentTime: 0,
                    totalTime: 0,
                    isFinished: false,
                    isPlay: false,
                    shootCutKeyId: -1
                }
            };
        // アップロードキャンセル
        case TimelineEvents.UpdateTimelineStatus:
            return {
                ...state,
                isSuccessUploadCancel: false,
            };
        case TimelineEvents.UpdateTimelineStatusCompleted:
            return {
                ...state,
                isSuccessUploadCancel: true,
            };

        // 直近のフッテージ取得
        case TimelineEvents.GetRecentFootagesComplete:
            return {
                ...state,
                recentFootages: action.footage,
            };

        // タイムライン作成
        case TimelineEvents.CreateTimelineStarted:
            return {
                ...state,
                isTimelineCreateStatus: true,
                duplicateNameError: "",
            };

        case TimelineEvents.CreateTimelineComplete:
            return {
                ...state,
                timeline: action.timeline,
                duplicateNameError: "",
                timelineInfoList: [action.timeline, ...state.timelineInfoList],
                isTimelineCreateStatus: false,
            };

        case TimelineEvents.CreateTimelineFailed:
            return {
                ...state,
                duplicateNameError: action.error,
                timelineInfoList: action.timeline,
                isTimelineCreateStatus: false,
            };

        // タイムライン削除
        case TimelineEvents.DeleteTimelineStarted:
            return {
                ...state,
                isLoading: true,
            };

        case TimelineEvents.DeleteTimelineComplete:
            return {
                ...state,
                timelineInfoList: state.timelineInfoList.filter((v) => v.id !== action.timelineId),
                isLoading: false,
            };

        // タイムライン再生開始
        case TimelineEvents.PlayTimeline:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    isPlay: true,
                },
            };

        // タイムライン再生一時停止
        case TimelineEvents.PauseTimeline:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    isPlay: false,
                },
            };

        // タイムライン再生終了
        case TimelineEvents.FinishTimeline:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    isPlay: false,
                    isFinished: true,
                },
            };

        // タイムラインのリプレイ
        case TimelineEvents.ReplayTimeline:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    currentTime: 0,
                    isFinished: false,
                },
            };

        // タイムラインのリセット
        case TimelineEvents.ResetTimeline:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    sourceLength: 0,
                    clipTimes: [],
                    currentTime: 0,
                    totalTime: 0,
                    isFinished: false,
                    isPlay: false,
                    isHandleKeyboard: true,
                    shootCutKeyId: -1
                },
            };

        // タイムライン時間更新
        case TimelineEvents.UpdateTimelinePlayInfo:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceLength: action.sourceLength ?? state.timelinePlayInfo.sourceLength,
                    clipTimes: action.clipTimes ?? state.timelinePlayInfo.clipTimes,
                    currentTime: action.currentTime ?? state.timelinePlayInfo.currentTime,
                    totalTime: action.totalTime ?? state.timelinePlayInfo.totalTime,
                    isFinished: action.isFinished ?? state.timelinePlayInfo.isFinished
                },
            };

        // タイムライン再生中のクリップINDEXを更新
        case TimelineEvents.UpdateTimelineSourceIndex:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: action.sourceIndex,
                },
            };

        // タイムラインの編集ショートカットキー番号を更新
        case TimelineEvents.UpdateTimelineShootCutKeyId:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    shootCutKeyId: action.shootCutKeyId,
                },
            };
            
        // タイムラインのkeyboardでvideo再生をコントロールするかどうか
        case TimelineEvents.SetIsHandleKeyboard:
            return {
                ...state,
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    isHandleKeyboard: action.flag,
                },
            };

        // タイムラインアップロード
        case TimelineEvents.UploadTimelineStarted:
            return {
                ...state,
                isLoadingUploadTimeline: true,
                isSuccessUploadTimeline: false,
                isFailedUploadTimeline: false,
            };

        case TimelineEvents.UploadTimelineComplete:
            return {
                ...state,
                uploadtimeline: action.timeline,
                isLoadingUploadTimeline: false,
                isSuccessUploadTimeline: true,
                isFailedUploadTimeline: false,
            };

        case TimelineEvents.UploadTimelineFailed:
            return {
                ...state,
                isLoadingUploadTimeline: false,
                isSuccessUploadTimeline: false,
                isFailedUploadTimeline: true,
            };

        case TimelineEvents.GetTimelineStarted:
            return {
                ...state,
                isLoading: true,
                timelineError: null,
                isSuccessGetTimeline: false,
                isFailedGetTimeline: false,
            };

        case TimelineEvents.GetTimelineComplete:
            return {
                ...state,
                timeline: action.result,
                isLoading: false,
                isSuccessGetTimeline: true,
            };

        case TimelineEvents.GetTimelineFailed:
            return {
                ...state,
                timelineError: action.result,
                isFailedGetTimeline: true,
            };

        case TimelineEvents.GetFootageListStarted:
            return {
                ...state,
                isLoading: true,
            };

        case TimelineEvents.GetFootageListComplete:
            return {
                ...state,
                footageList: action.result,
                isLoading: false,
            };

        case TimelineEvents.GetFootageVideoStarted:
            return {
                ...state,
                footageUrl: "",
            };

        case TimelineEvents.GetFootageVideoComplete:
            return {
                ...state,
                footageUrl: action.result,
            };

        case TimelineEvents.GetThumbnailComplete:
            return {
                ...state,
                thumbnail: action.result,
            };

        case TimelineEvents.GetUsableDirListComplete:
            return {
                ...state,
                directoryList: action.result,
            };

        case DirectoryEvents.UploadFootageStarted:
            return {
                ...state,
                isLoadingUploadFootage: true,
            };

        case DirectoryEvents.UploadFootageComplete:
            return {
                ...state,
                isLoadingUploadFootage: false,
            };

        case TimelineEvents.AddClipToTimelineStarted:
            return {
                ...state,
                isAddClipToTimelineComplete: false,
                isLoading: true,
            };

        case TimelineEvents.AddClipToTimelineComplete:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    currentTime: 0,
                    sourceLength: action.clipList.length,
                    clipTimes: GetClipTimesFromCliplist(action.clipList),
                    totalTime: GetTotalTimeFromClipList(action.clipList),
                    isFinished: false,
                    isPlay: false,
                    shootCutKeyId: -1
                },
                isAddClipToTimelineComplete: true,
                isChangeClipListHistory: false,
                isLoading: false,
            };

        case TimelineEvents.ClipMoveStarted:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                },
                isClipMove: false,
                isLoading: true,
            };

        case TimelineEvents.ClipMoveComplete:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    currentTime: 0,
                    isFinished: false,
                    isPlay: false,
                    shootCutKeyId: -1
                },
                isClipMove: true,
                isChangeClipListHistory: false,
                isLoading: false,
            };

        case TimelineEvents.DeleteClipStarted:
            return {
                ...state,
                isSuccessDeleteClip: false,
                isLoading: true,
            };

        case TimelineEvents.DeleteClipComplete:
            return {
                ...state,
                isSuccessDeleteClip: true,
                isChangeClipListHistory: false,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    currentTime: 0,
                    sourceLength: action.clipList.length,
                    clipTimes: GetClipTimesFromCliplist(action.clipList),
                    totalTime: GetTotalTimeFromClipList(action.clipList),
                    isFinished: false,
                    isPlay: false,
                    shootCutKeyId: -1
                },
                isLoading: false,
            };

        case TimelineEvents.CutClipComplete:
            return {
                ...state,
                isChangeClipListHistory: false,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    // sourceIndex: 0,
                    // currentTime: 0,
                    sourceLength: action.clipList.length,
                    clipTimes: GetClipTimesFromCliplist(action.clipList),
                    totalTime: GetTotalTimeFromClipList(action.clipList),
                    // isFinished: false,
                    // isPlay: false,
                    shootCutKeyId: -1
                },
            };

        // 失敗処理は今後対応する予定
        // case TimelineEvents.CutClipFailed:
        //     return {
        //         ...state,
        //         timeline: {
        //             ...state.timeline,
        //             isCutClipComplete: false
        //         }
        //     };

        case VideoInfoEvents.UpdateVideoInfoComplete:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    timeline: {
                        ...state.timeline?.timeline,
                    },
                },
            };

        case TimelineEvents.EditClipStarted:
            return {
                ...state,
                isAddClipToTimelineComplete: false,
                isLoading: true,
            };

        case TimelineEvents.EditClipComplete:
            console.log("action: ", action);
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                timelinePlayInfo: {
                    ...state.timelinePlayInfo,
                    sourceIndex: 0,
                    currentTime: 0,
                    sourceLength: action.clipList.length,
                    clipTimes: GetClipTimesFromCliplist(action.clipList),
                    totalTime: GetTotalTimeFromClipList(action.clipList),
                    shootCutKeyId: -1
                },
                isAddClipToTimelineComplete: true,
                isChangeClipListHistory: false,
                isLoading: false,
            };
        // タイライム名変更
        case TimelineEvents.RenameTimelineStarted:
            return {
                ...state,
                isSuccessGetTimelines: false,
                isLoading: true,
            };

        case TimelineEvents.RenameTimelineComplete:
            return {
                ...state,
                timelineInfoList: action.timeline,
                isSuccessGetTimelines: true,
                isLoading: false,
            };

        case TimelineEvents.UpdateTimelineClipListComplete:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    clipList: action.clipList,
                },
                isChangeClipListHistory: true,
                isLoading: false
            };
        case TimelineEvents.UpdateTimelineClipList:
            return {
                ...state,
                isLoading: true
            };

        default:
            return state;
    }
}

export default timeline;
